<template>
  <UiComponentWrapper
    class="page"
    :class="{ daylight: isOfTypeDaylightBackground }"
  >
    <!-- [Conditional] Modal > Share -->
    <!-- Only displayed on page of type classic background -->
    <UiComponentModal
      v-if="displayModal"
      ref="modal"
      class="share"
      @modal:close="displayModal = false"
    >
      <template #content>
        <UiComponentParagraph size="s">
          {{ data.hub.modalShareOn }}
        </UiComponentParagraph>
        <UiComponentWrapper class="actions">
          <AtomButton
            class="twitter"
            force-no-follow
            :to="shareOnTwitter"
            @click="
              track('Share clicked', {
                category: 'Button',
                label: 'Twitter content',
              })
            "
          >
            <UiComponentImage
              :alt="data.hub.modalLogoTwitter.alt"
              :expected-size="24"
              :height="data.hub.modalLogoTwitter.height"
              :source="data.hub.modalLogoTwitter.url"
              :width="data.hub.modalLogoTwitter.width"
            />
          </AtomButton>
          <AtomButton
            class="facebook"
            force-no-follow
            :to="shareOnFacebook"
            @click="
              track('Share clicked', {
                category: 'Button',
                label: 'Facebook content',
              })
            "
          >
            <UiComponentImage
              :alt="data.hub.modalLogoFacebook.alt"
              :expected-size="24"
              :height="data.hub.modalLogoFacebook.height"
              :source="data.hub.modalLogoFacebook.url"
              :width="data.hub.modalLogoFacebook.width"
            />
          </AtomButton>
          <AtomButton
            class="linked-in"
            force-no-follow
            :to="shareOnLinkedIn"
            @click="
              track('Share clicked', {
                category: 'Button',
                label: 'Linkedin content',
              })
            "
          >
            <UiComponentImage
              :alt="data.hub.modalLogoLinkedIn.alt"
              :expected-size="24"
              :height="data.hub.modalLogoLinkedIn.height"
              :source="data.hub.modalLogoLinkedIn.url"
              :width="data.hub.modalLogoLinkedIn.width"
            />
          </AtomButton>
        </UiComponentWrapper>
        <UiComponentParagraph size="s">
          {{ data.hub.modalCopyLink }}
        </UiComponentParagraph>
        <UiComponentWrapper
          class="copy-to-clipboard"
          :class="{ copied }"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Copy link',
            });
            copyURLToClipboard();
          "
        >
          <UiComponentWrapper class="button">
            <UiComponentIcon type="link" />
          </UiComponentWrapper>
          <UiComponentParagraph
            class="url"
            size="s"
          >
            {{ url }}
          </UiComponentParagraph>
          <UiComponentWrapper class="interaction">
            {{ data.hub.modalCopied }}
          </UiComponentWrapper>
        </UiComponentWrapper>
      </template>
    </UiComponentModal>

    <!-- Section > Jumbotron -->
    <UiComponentContainer class="inner">
      <UiComponentWrapper class="jumbotron">
        <UiComponentWrapper class="metadata">
          <NuxtLink
            :prefetch="false"
            :to="
              localePath({
                name: 'virtual-background-library-category-slug',
                params: {
                  slug: data.page.category.slug,
                },
              })
            "
          >
            <UiComponentTag type="secondary">
              {{
                data.page.category.categoryShortName
              }}
            </UiComponentTag>
          </NuxtLink>
          <UiComponentHeading :level="1">
            {{
              data.page.backgroundTitle
            }}
          </UiComponentHeading>
          <UiComponentParagraph class="description">
            {{
              data.page.backgroundDescription
            }}
          </UiComponentParagraph>
          <UiComponentWrapper
            v-if="optionalFormatAndResolution"
            class="more"
          >
            <UiComponentParagraph size="s">
              {{
                optionalFormatAndResolution
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="actions">
            <AtomButton
              download
              size="l"
              :to="`${data.page.backgroundImageForWebsite.url}?w=1328&h=720&fit=crop&dl=${data.page.slug}.jpg`"
              @click="onDownload"
            >
              <UiComponentIcon type="download" />{{
                isOfTypeDaylightBackground
                  ? data.hub.daylightBackgroundDownload
                  : data.hub.backgroundDownload
              }}
            </AtomButton>
            <AtomButton
              v-if="isOfTypeDaylightBackground"
              size="l"
              to="#"
              type="text"
              @click="slideToDaylightBackgroundInfo"
            >
              <UiComponentIcon type="zap" />{{
                data.hub.daylightBackgroundAnchorCta
              }}
            </AtomButton>
            <AtomButton
              v-else
              size="l"
              to="#"
              type="text"
              @click="
                track('Share popup clicked', {
                  category: 'Button',
                  label: 'Share CTA',
                });
                displayModal = true;
              "
            >
              <UiComponentIcon type="share-2" />{{ data.hub.backgroundShare }}
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentWrapper
          v-if="isOfTypeDaylightBackground"
          class="background"
          :style="{ aspectRatio: `${imageWidth} / ${imageHeight}` }"
        >
          <UiComponentWrapper
            class="animation"
            :class="{
              show: animationHasBeenIniatilized,
            }"
          >
            <img
              v-for="(image, index) in data.page.animation"
              :key="`frame-${index}-${imageWidth}x${imageHeight}`"
              :alt="
                image.alt || `${data.page.backgroundTitle} frame ${index}`
              "
              class="frame"
              :class="[
                { active: animationIndex === index },
                {
                  next:
                    (animationIndex + 1) % data.page.animation.length
                    === index,
                },
              ]"
              :height="imageHeight"
              :src="`${image.url}?h=${imageHeight}&w=${imageWidth}&fit=crop&fm=png&auto=format`"
              :srcset="`${image.url}?h=${imageHeight}&w=${imageWidth}&fit=crop&fm=png&auto=format&q=80&dpr=1 1x, ${image.url}?h=${imageHeight}&w=${imageWidth}&fit=crop&fm=png&auto=format&q=40&dpr=2 2x, ${image.url}?h=${imageHeight}&w=${imageWidth}&fit=crop&fm=png&auto=format&q=20&dpr=3 3x`"
              :width="imageWidth"
            >
            <UiComponentWrapper class="states">
              <img
                alt="Morning"
                :class="{ active: animationIndex === 0 || animationIndex === 1 }"
                height="16"
                loading="lazy"
                src="@/assets/svg/morning.svg"
                width="16"
              >
              <img
                alt="Noon"
                :class="{ active: animationIndex === 2 || animationIndex === 3 }"
                height="16"
                loading="lazy"
                src="@/assets/svg/noon.svg"
                width="16"
              >
              <img
                alt="Afternoon"
                :class="{ active: animationIndex === 4 || animationIndex === 5 }"
                height="16"
                loading="lazy"
                src="@/assets/svg/afternoon.svg"
                width="16"
              >
              <img
                alt="Night"
                :class="{ active: animationIndex === 6 || animationIndex === 7 }"
                height="16"
                loading="lazy"
                src="@/assets/svg/night.svg"
                width="16"
              >
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentWrapper
          v-else
          class="background"
          :style="{ aspectRatio: `${imageWidth} / ${imageHeight}` }"
        >
          <UiComponentImage
            :alt="data.page.backgroundImageForWebsite.alt"
            :expected-size="imageWidth"
            :height="data.page.backgroundImageForWebsite.height"
            :source="data.page.backgroundImageForWebsite.url"
            :width="data.page.backgroundImageForWebsite.width"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>

      <!-- [Conditional] Section > Daylight background -->
      <!-- Only displayed on page of type daylight background -->
      <UiComponentWrapper class="daylight-background">
        <UiComponentWrapper
          v-observe-visibility="onVisibilityChanged"
          class="video"
        >
          <iframe
            v-if="showIframe"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            class="loaded"
            height="100%"
            loading="lazy"
            :src="data.componentDaylightBackgroundInfo.videoLink"
            title="YouTube video player"
            width="100%"
          />
        </UiComponentWrapper>
        <UiComponentWrapper class="content">
          <UiComponentHeading
            as="paragraph"
            :level="3"
          >
            {{
              data.componentDaylightBackgroundInfo.title
            }}
          </UiComponentHeading>
          <UiComponentParagraph>{{ data.componentDaylightBackgroundInfo.text }}</UiComponentParagraph>
          <AtomButton
            size="l"
            to="https://app.livestorm.co/#/signup"
            type="text"
            @click="
              track('Signup clicked', {
                category: 'Button',
                label: 'Daylight background section CTA',
              })
            "
          >
            {{ data.componentDaylightBackgroundInfo.ctaWording }}<UiComponentIcon type="arrow-right" />
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <!-- Section > How to add a virtual background -->
      <OrganismVirtualBackgroundInstallationBlock
        :cards="installationCards"
        :cta="installationCallToAction"
      >
        <template #title>
          {{ data.componentHowToAddVirtualBackground.howToTitle }}
        </template>
      </OrganismVirtualBackgroundInstallationBlock>

      <!-- Section > Related background -->
      <UiComponentWrapper
        v-if="cards.length > 0"
        class="related-backgrounds"
      >
        <UiComponentWrapper class="head">
          <UiComponentWrapper class="line" />
          <UiComponentParagraph size="s">
            {{
              data.hub.backgroundRelatedTitle
            }}
          </UiComponentParagraph>
          <UiComponentWrapper class="line" />
        </UiComponentWrapper>
        <UiComponentWrapper class="body">
          <MoleculeVirtualBackgroundCard
            v-for="{
              id,
              image,
              isDaylightBackground,
              tag,
              paragraph,
              slug,
            } in cards"
            :key="id"
            as="paragraph"
            :image="image"
            :is-daylight="isDaylightBackground"
            :to="
              localePath({
                name: 'virtual-background-library-slug',
                params: {
                  slug,
                },
              })
            "
          >
            <template #tag>
              {{ tag }}
            </template>
            <template #paragraph>
              {{ paragraph }}
            </template>
          </MoleculeVirtualBackgroundCard>
        </UiComponentWrapper>
        <AtomButton
          :to="
            localePath({
              name: 'virtual-background-library-category-slug',
              params: {
                slug: data.page.category.slug,
              },
            })
          "
          type="text"
        >
          {{ data.hub.backgroundRelatedCtaTitle }} <UiComponentIcon type="arrow-right" />
        </AtomButton>
      </UiComponentWrapper>

      <!-- Section > Get started -->
      <UiComponentWrapper class="get-started">
        <MoleculeCallToAction
          :call-to-action="{
            ...primaryCallToAction,
            label: 'Small section CTA',
          }"
          :pattern-desktop="data.componentGetStarted.backgroundImage"
          :pattern-mobile="data.componentGetStarted.backgroundImage"
        >
          <template #heading>
            {{ data.componentGetStarted.title }}
          </template>
          <template #paragraph>
            {{ data.componentGetStarted.paragraph }}
          </template>
        </MoleculeCallToAction>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="platforms.length > 0"
        class="explore"
      >
        <UiComponentHeading :level="2">
          {{ data.hub.backgroundExploreTitle }}
        </UiComponentHeading>
        <UiComponentWrapper class="platforms">
          <AtomButton
            v-for="{ id, slug, platformShortName } in platforms"
            :key="id"
            :to="
              localePath({
                name: 'virtual-background-library-platform-slug',
                params: {
                  slug,
                },
              })
            "
            type="text"
          >
            {{ platformShortName }}
          </AtomButton>
        </UiComponentWrapper>
        <AtomButton
          v-if="platforms.length > 7"
          to="#"
          type="text"
          class="action"
          @click="showAllPlatforms = !showAllPlatforms"
        >
          {{ showLessOrShowMore }}
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Related Posts -->
    <OrganismHighlightedPosts
      v-if="data.page.relatedPosts.length >= 4 || data.componentRelatedPosts.items.length > 0"
      class="our-guides"
      :items="
        data.page.relatedPosts.length >= 4
          ? data.page.relatedPosts
          : data.componentRelatedPosts.items
      "
      :minutes-read="data.componentGlobal.minRead"
    >
      {{ data.page.relatedPostsTitle || data.componentRelatedPosts.title }}
    </OrganismHighlightedPosts>

    <!-- Section > Our Guides -->
    <OrganismHighlightedGuides
      v-if="data.allPillarPages.length > 0 || data.page.ourGuides.length > 0"
      class="our-guides"
      :items="
        data.page.ourGuides.length > 0
          ? data.page.ourGuides
          : data.allPillarPages
      "
    >
      {{ data.page.ourGuidesTitle || data.componentOurGuides.title }}
    </OrganismHighlightedGuides>
  </UiComponentWrapper>
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
  middleware: [
    'check-if-the-virtual-background-exists',
  ],
})

// i18n
defineI18nRoute({
  locales: ['en', 'fr'],
  paths: {
    en: '/virtual-background-library/[slug]',
    fr: '/arriere-plan-virtuel/[slug]',
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { fullPath, meta, params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Directives
const { vObserveVisibility } = useVObserveVisibility()

if (['es'].includes(locale.value)) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Async Data
const asyncKey = `page-virtual-background-library-[slug]-${params.slug}-${locale.value}`
const { data } = await useAsyncData(asyncKey, async () => {
  return Promise.all([
    $fetch(`/api/content/pages/virtual-background-index/${locale.value}`, { query: { preview: state.preview } }),
    $fetch(`/api/content/pages/virtual-background-list/${locale.value}`, { query: { preview: state.preview } }),
  ])
}, {
  transform: ([{ allPillarPages, allPlatforms, componentDaylightBackgroundInfo, componentGetStarted, componentGlobal, componentHowToAddVirtualBackground, componentOurGuides, componentRelatedPosts, home, page: hub }, allBackgrounds]) => {
    const page = allBackgrounds.find(({ slug }) => slug === params.slug)
    return {
      allPillarPages,
      allPlatforms,
      componentDaylightBackgroundInfo,
      componentGetStarted,
      componentGlobal,
      componentHowToAddVirtualBackground,
      componentOurGuides,
      componentRelatedPosts,
      home,
      hub,
      page,
      relatedVirtualBackgrounds: allBackgrounds
        .filter(({ slug }) => slug !== params.slug)
        .filter(({ category }) => category.id === page.category.id)
        .slice(0, 4),
    }
  },
})

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Meta
const { alternativeVersions } = data.value.page
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale, value: slug }) => ({
    ...accumator,
    [locale]: { slug },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useHead({
  link: head.value.link
    .filter((link) => {
      return link.rel === 'canonical'
        || (link.rel === 'alternate' && link.hreflang === 'x-default')
        || (link.rel === 'alternate' && locales.includes(link.hreflang))
    })
    .map((link) => {
      return {
        ...link,
        href: !locales.includes('en') && link.id === 'i18n-xd'
          ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
          : link.href,
      }
    }),
  meta: head.value.meta.filter((meta) => {
    return meta.property === 'og:url'
      || meta.property === 'og:locale'
      || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
  }),
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('virtual-background-library')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.page.trackingBreadcrumbTitle || data.value.page.backgroundTitle,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

useSeoMeta({
  title: data.value.page.seoTitle,
  description: data.value.page.seoDescription,
  ogTitle: data.value.page.seoTitle,
  ogDescription: data.value.page.seoDescription,
  twitterTitle: data.value.page.seoTitle,
  twitterDescription: data.value.page.seoDescription,
})

// Refs
const animationHasBeenIniatilized = ref(false)
const animationIndex = ref(1)
const animationIntervalId = ref(null)
const copied = ref(false)
const displayModal = ref(false)
const showAllPlatforms = ref(false)
const showIframe = ref(false)
const timeoutId = ref(null)

// Computed Properties
const animationAspectRatio = computed(() => {
  return (
    data.value.page.backgroundImageForWebsite.width
    / data.value.page.backgroundImageForWebsite.height
  )
})

const cards = computed(() => {
  return data.value.relatedVirtualBackgrounds
    .filter(({ slug }) => !!slug)
    .map(
      ({
        id,
        backgroundTitle: paragraph,
        backgroundImageForWebsite: image,
        category: { categoryShortName: tag },
        isDaylightBackground,
        slug,
      }) => ({
        id,
        image,
        isDaylightBackground: !!isDaylightBackground,
        tag,
        paragraph,
        slug,
      }),
    )
})

const imageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 48
    default:
      return 730
  }
})

const imageHeight = computed(() => {
  return Number.parseInt(imageWidth.value / animationAspectRatio.value)
})

const installationCallToAction = computed(() => {
  return {
    to: data.value.componentHowToAddVirtualBackground.ctaLink,
    slot: data.value.componentHowToAddVirtualBackground.ctaTitle,
  }
})

const installationCards = computed(() => {
  return data.value.componentHowToAddVirtualBackground.howToSteps?.map(
    ({ id, image, number, text }) => ({
      id,
      image,
      paragraph: text,
      step: number,
    }),
  )
})

const isOfTypeDaylightBackground = computed(() => {
  return !!data.value.page.isDaylightBackground
})

const optionalFormatAndResolution = computed(() => {
  const wording = []

  if (data.value.hub.backgroundResolution) {
    wording.push(
      `${data.value.hub.backgroundResolution} : ${data.value.page.backgroundImageForWebsite?.width}x${data.value.page.backgroundImageForWebsite?.height}`,
    )
  }

  if (data.value.hub?.backgroundFormat) {
    const extension = data.value.page?.backgroundImageForWebsite?.url
      ?.split('.')
      ?.pop()
      ?.toUpperCase()
    wording.push(`${data.value.hub?.backgroundFormat} : ${extension}`)
  }

  return wording.length === 0 ? undefined : wording.join(' ')
})

const platforms = computed(() => {
  return data.value.allPlatforms.slice(
    0,
    showAllPlatforms.value ? data.value.allPlatforms.length : 6,
  )
})

const primaryCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaTryForFree,
      }
})

const shareOnFacebook = computed(() => {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    url.value,
  )}`
})

const shareOnLinkedIn = computed(() => {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    url.value,
  )}`
})

const shareOnTwitter = computed(() => {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    url.value,
  )}`
})

const showLessOrShowMore = computed(() => {
  return showAllPlatforms.value ? data.value.page.showLess : data.value.page.showMore
})

const url = computed(() => {
  return `${publicRuntimeConfig.baseUrl}${fullPath}`
})

// Watchers
watch(() => displayModal.value, (shouldBeVisible) => {
  if (shouldBeVisible) {
    document.body.classList.add('no-scroll')
  }
  else {
    document.body.classList.remove('no-scroll')
  }
})

// Methods
function copyURLToClipboard() {
  if (import.meta.client) {
    navigator.clipboard.writeText(url.value)
    copied.value = true
    timeoutId.value = window.setTimeout(() => {
      copied.value = false
      timeoutId.value = undefined
    }, 2000)
  }
}

function startAnimation() {
  animationIntervalId.value = window.setInterval(() => {
    animationIndex.value += 1
    animationIndex.value %= data.value.page.animation.length
  }, 2500)
}

function onDownload() {
  track('Download virtual background clicked', {
    category: 'Button',
    background: data.value.page.backgroundTitle,
  })
}

function onVisibilityChanged(isVisible) {
  if (isVisible && !showIframe.value) {
    showIframe.value = true
  }
}

function slideToDaylightBackgroundInfo() {
  const $daylightBackgroundInfo = document.querySelector(
    '.page .daylight-background',
  )
  const { offsetTop: featuresOffsetTop } = $daylightBackgroundInfo
  const { offsetHeight: navigationOffsetHeight }
    = document.querySelector('.app__layout-header')
  const top = featuresOffsetTop - navigationOffsetHeight
  window.scrollTo({ top, behavior: 'smooth' })
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (animationIntervalId.value) {
    window.clearInterval(animationIntervalId.value)
  }

  if (timeoutId.value) {
    window.clearTimeout(timeoutId.value)
  }

  if (document.body.classList.contains('no-scroll')) {
    document.body.classList.remove('no-scroll')
  }
})

onMounted(() => {
  animationHasBeenIniatilized.value = true
  page(data.value.page.trackName || data.value.page.trackNameFallback)
  setAvailableLanguages(data.value.page.alternativeVersions.map(({ locale }) => locale))
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('virtual-background-library'),
      name: data.value.hub.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || data.value.page.backgroundTitle,
    },
  ])

  nextTick(() => {
    startAnimation()
  })
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col items-center;

  &.daylight {
    .jumbotron {
      @screen xl {
        @apply items-center;
      }

      .metadata {
        .actions {
          @apply flex-col mt-8;

          @screen md {
            @apply flex-row items-center;
          }

          @screen xl {
            @apply flex-col items-start;
          }

          .button {
            & + .button {
              @apply h-7 mt-6;

              @screen md {
                @apply ml-6 mt-0;
              }

              @screen xl {
                @apply ml-0 mt-6;
              }
            }
          }
        }
      }

      .background {
        @apply bg-grey-blue-100 rounded-4xl overflow-hidden;

        .animation {
          @apply relative w-full h-full opacity-0 z-0 transition-opacity duration-500 ease-out;

          &::after {
            @apply absolute flex bottom-0 left-0 right-0 w-full h-[121px] z-30;
            background: linear-gradient(
              180deg,
              rgba(0, 0, 0, 0) 0%,
              rgba(0, 0, 0, 0.2) 100%
            );

            @screen xl {
              @apply content-[''];
            }
          }

          &.show {
            @apply opacity-100;
          }

          .frame {
            @apply absolute left-0 top-0 opacity-0 z-0 transition-opacity duration-[2500ms] ease-linear;

            &.active {
              @apply opacity-100 z-10;
            }

            &.next {
              @apply opacity-100 z-20;
            }
          }

          .states {
            @apply hidden absolute bottom-[18px] left-[117px] right-[117px] justify-between w-[496px] h-4 z-40;

            @screen xl {
              @apply flex;
            }

            img {
              @apply opacity-50 transition-opacity duration-[2500ms] ease-in-out;

              &.active {
                @apply opacity-100;
              }
            }
          }
        }
      }
    }
  }

  .modal {
    :deep(.box) {
      @apply p-4 pt-3;
      border-radius: 24px;
      width: 296px;

      .close {
        @apply hidden;
      }

      .body {
        @apply items-start;

        p {
          @apply font-medium text-grey-blue-600 leading-6;
        }

        .actions {
          @apply mt-2 mb-4;

          .button {
            @apply w-full mx-2;
            border-radius: 10px;

            &.twitter {
              @apply ml-0 bg-twitter-100;

              &:hover {
                @apply bg-twitter-200;
              }
            }

            &.facebook {
              @apply bg-facebook-100;

              &:hover {
                @apply bg-facebook-200;
              }
            }

            &.linked-in {
              @apply mr-0 bg-linkedin-100;

              &:hover {
                @apply bg-linkedin-200;
              }
            }

            picture {
              @apply rounded-none;
            }
          }
        }

        .copy-to-clipboard {
          @apply relative flex-row items-center w-full h-10 mt-2 p-1 pr-8 bg-grey-blue-100 border border-grey-blue-300 leading-8 rounded cursor-pointer;

          &.copied {
            .interaction {
              @apply visible opacity-100;
            }
          }

          .button {
            @apply flex-shrink-0 items-center justify-center w-8 h-8 bg-white;
            border-radius: 8px;

            i {
              @apply font-medium text-grey-blue-600;
            }
          }

          .url {
            @apply ml-2 font-normal text-grey-blue-400 leading-6 whitespace-nowrap truncate;
          }

          .interaction {
            @apply absolute top-0 right-0 flex w-auto h-7 px-2 bg-winter-green-900 text-white font-medium text-xs leading-7 invisible opacity-0 transition-all duration-500 ease-in-out;
            border-radius: 8px;
            content: attr(data-copied);
            transform: translateY(calc(-100% - 6px));

            &::before {
              @apply absolute flex bg-winter-green-900 w-2 h-2 rounded-sm;
              bottom: -2px;
              content: "";
              right: 50%;
              transform: translateX(6px) rotate(45deg);
            }
          }
        }
      }
    }
  }

  .inner {
    @apply flex-col;
  }

  .jumbotron {
    @apply flex-col pt-14;

    @screen md {
      @apply pt-26;
    }

    @screen xl {
      @apply flex-row flex-wrap justify-between;
    }

    .metadata {
      @apply flex-col;

      @screen xl {
        width: 510px;
      }

      a,
      .tag {
        @apply self-start;
      }

      h1 {
        @apply font-medium text-2xl leading-8 mt-4;

        @screen md {
          @apply text-3xl leading-10;
        }
      }

      .description {
        @apply mt-4 text-grey-blue-600;
      }

      .more {
        @apply inline-flex mt-4;

        p {
          @apply text-grey-blue-600 leading-6;
        }
      }

      .actions {
        @apply flex-col mt-4;

        @screen md {
          @apply flex-row;
        }

        @screen mdminus {
          @apply mt-8;
        }

        @screen mdplus {
          @apply mt-12;
        }

        .primary {
          @apply w-full;

          @screen md {
            @apply w-auto;
          }

          i {
            @apply text-base text-white mr-2;
          }
        }

        .text {
          @apply mt-8 text-livestorm-blue-700;

          @screen md {
            @apply mt-0 ml-6 px-0;
          }

          i {
            @apply text-base text-livestorm-blue-700 mr-2;
          }
        }
      }
    }

    .background {
      @apply flex-col mt-10;

      @screen xl {
        @apply mt-0;
        width: 730px;
      }

      picture {
        @apply border-2 border-grey-blue-200;
      }
    }
  }

  .daylight-background,
  .virtual-background-installation-block,
  .related-backgrounds,
  .get-started,
  .explore {
    @apply mt-20;

    @screen mdminus {
      @apply mt-20;
    }

    @screen mdplus {
      @apply mt-34;
    }
  }

  .daylight-background {
    @apply flex-col p-4 pb-6 border-2 border-grey-blue-200 rounded-4xl;

    @screen md {
      @apply p-6;
    }

    @screen xl {
      @apply flex-row items-center justify-between;
    }

    .video {
      @apply relative pb-16/9 bg-grey-blue-200 rounded-2xl overflow-hidden;

      @screen xl {
        @apply max-w-[596px];
        padding-bottom: calc((596 / 1280) * 56.25%);
      }

      iframe {
        @apply absolute h-full w-full opacity-0 transition-opacity duration-1000 ease-in-out;

        &.loaded {
          @apply opacity-100;
        }
      }
    }

    .content {
      @apply flex-col mt-4;

      @screen md {
        @apply mt-6;
      }

      @screen xl {
        @apply max-w-[580px] mt-0;
      }

      .h3 + p {
        @apply mt-4 text-grey-blue-600;

        @screen md {
          @apply mt-6;
        }
      }

      .button {
        @apply self-start h-[50px] mt-6 px-0 font-bold;
      }
    }
  }

  .virtual-background-installation-block {
    :deep(h2) {
      @screen md {
        @apply text-5xl leading-16;
      }

      @screen xl {
        @apply text-center;
        max-width: 840px;
      }

      strong {
        @apply text-winter-green-900 !important;
      }
    }
  }

  .related-backgrounds {
    @apply flex-col;

    .head {
      @apply flex flex-row justify-between items-center;

      .line {
        @apply flex-grow border-t-2 border-grey-blue-200 w-auto;
      }

      p {
        @apply font-medium text-sm text-grey-blue-700 px-5 uppercase;
      }
    }

    .body {
      @apply flex-wrap mt-20;

      @screen xl {
        @apply justify-center;
      }

      .virtual-background-card {
        @apply mt-12 w-full;

        @screen md {
          @apply mt-10;
          width: calc(50% - 20px);
        }

        @screen xl {
          width: 290px;
        }

        &:first-child {
          @apply mt-0;
        }

        &:nth-child(2) {
          @screen md {
            @apply mt-0;
          }
        }

        &:nth-child(3) {
          @screen xl {
            @apply mt-0;
          }
        }

        &:nth-child(4) {
          @screen xl {
            @apply mt-0;
          }
        }

        &:nth-child(2n) {
          @screen md {
            @apply ml-10;
          }
        }

        &:nth-child(4n + 3) {
          @screen xl {
            @apply ml-10;
          }
        }
      }
    }

    .text {
      @apply mt-20 text-livestorm-blue-700;

      i {
        @apply text-livestorm-blue-700;
      }
    }
  }

  .get-started {
    @apply justify-center;

    .small-call-to-action {
      @screen lg {
        max-width: 792px;
      }
    }
  }

  .explore {
    @apply flex-col items-center;

    h2 {
      @apply font-normal text-center !important;

      @screen md {
        @apply text-5xl leading-16;
      }

      @screen xl {
        max-width: 1000px;
      }
    }

    .platforms {
      @apply flex-wrap justify-center items-center gap-6 max-w-[840px] mt-12;

      @screen md {
        @apply gap-y-4;
      }

      .text {
        @apply h-6 text-livestorm-blue-700 font-bold text-sm leading-6;
      }
    }

    .action {
      @apply font-bold;

      @screen md {
        @apply mt-2;
      }

      @screen xl {
        @apply mt-4;
      }
    }
  }
}
</style>
